// ADV-266 — the rows of the comparison table (Figma "KONTO | 04 Vergleich": desktop 64223:12612,
// mobile 64223:12717), built from what the page already loads: the comparison payload
// (UserActionsAPI.getComparedVehicles → getComparableVehicle), where `details` is the car and
// `price` the computed financing / leasing / buy config (the numbers ComparePrice showed), plus the
// cars' views ComparePage loads from the stats.
// A row none of the cars has a value for is left out, and so is a section left without rows.
import _get from 'lodash/get';
import { getDecoratedProps, getActualHighlights, getPredictablePrice } from '@bytebrand/fe-ui-core-autobahn/utils';
import { co2ClassLetter, mapRating } from '@bytebrand/fe-ui-core-autobahn/data/toAutobahnCar';
import euroIcon from './icons/row-euro.svg';
import tagIcon from './icons/row-tag.svg';
import starIcon from './icons/row-star.svg';
import mileageIcon from './icons/row-mileage.svg';
import registrationIcon from './icons/row-registration.svg';
import ownersIcon from './icons/row-owners.svg';
import gearboxIcon from './icons/row-gearbox.svg';
import powerIcon from './icons/row-power.svg';
import fuelIcon from './icons/row-fuel.svg';
import driveIcon from './icons/row-drive.svg';
import doorIcon from './icons/row-door.svg';
import colorIcon from './icons/row-color.svg';
import inspectionIcon from './icons/row-inspection.svg';
import leafIcon from './icons/row-leaf.svg';
import engineIcon from './icons/row-engine.svg';
import truckIcon from './icons/row-truck.svg';
import cameraIcon from './icons/row-camera.svg';
import pinIcon from './icons/row-pin.svg';
import sparkleIcon from './icons/row-sparkle.svg';
const ns = (key) => `ComparePage:cmp.${key}`;
const NONE = { kind: 'none' };
const num = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : undefined);
const positive = (value) => { const n = num(value); return n != null && n > 0 ? n : undefined; };
// grouped whole euros with a no-break space, as the other md3 prices
const euro = (value) => `${Math.round(value).toLocaleString('de-DE')} €`;
const decimal = (value) => value.toLocaleString('de-DE');
const text = (value) => (value ? { kind: 'text', text: value } : NONE);
/** fuel-aware unit of the combined consumption (EV: kWh, gas fuels: kg) */
const KG_FUELS = ['selector_fuel_naturalGas', 'selector_fuel_carGas', 'selector_fuel_hydrogen', 'selector_fuel_ethanol'];
const consumptionText = (consumption = {}) => {
    const fuel = String(consumption.fuel || '');
    if (fuel === 'selector_fuel_electrical') {
        const kwh = positive(consumption.wltpPowerCombined) ?? positive(consumption.consumptionPowerCombined);
        return kwh != null ? `${decimal(kwh)} kWh/100 km` : undefined;
    }
    const value = positive(consumption.wltpCombined) ?? positive(consumption.consumptionCombined);
    if (value == null)
        return undefined;
    return `${decimal(value)} ${KG_FUELS.indexOf(fuel) >= 0 ? 'kg' : 'l'}/100 km`;
};
const isPresent = (cell) => cell.kind !== 'none';
/** what the "Nur Unterschiede anzeigen" filter compares */
export const cellValue = (cell) => {
    switch (cell.kind) {
        case 'text': return cell.text;
        case 'rate': return cell.amount;
        case 'rating': return cell.label;
        case 'co2': return cell.letter;
        case 'chips': return cell.items.join('|');
        default: return '';
    }
};
export const isSameAcross = (row) => row.cells.every((cell) => cellValue(cell) === cellValue(row.cells[0]));
/** the header columns: make + model, the trim line, the main photo */
export const buildCompareCars = (vehicles, photoOf) => vehicles.map((vehicle) => {
    const { make = '', model = '', subModel = '' } = vehicle.title || {};
    return {
        id: vehicle.id,
        name: [make, model].filter(Boolean).join(' '),
        subtitle: subModel || '',
        photo: photoOf(vehicle),
    };
});
export const buildCompareSections = (vehicles, t, language, views = {}) => {
    const na = t('vehicleProps:value.na');
    const cars = vehicles.map((vehicle) => {
        const details = vehicle.details || {};
        return {
            details,
            price: vehicle.price || {},
            views: views[vehicle.id],
            decorated: getDecoratedProps(details, t, language),
        };
    });
    // decoratedProps values, with their "N/A" turned into the design's dash
    const prop = (decorated, key) => {
        const value = decorated[key] && decorated[key].value;
        return typeof value === 'string' && value && value !== na ? { kind: 'text', text: value } : NONE;
    };
    const row = (key, icon, cell, extra = {}) => ({
        key,
        label: t(ns(`rows.${key}`)),
        icon,
        cells: cars.map(cell),
        ...extra,
    });
    const unit = t(ns('values.perMonth'));
    const priceRows = [
        row('financing', euroIcon, ({ price }) => {
            const rate = positive(_get(price, 'financing.monthlyInstallment'));
            return rate != null ? { kind: 'rate', amount: euro(rate), unit } : NONE;
        }),
        // the struck-through rate ComparePrice showed (common.isStrikeShown)
        row('uvpRate', euroIcon, ({ price }) => {
            const old = positive(_get(price, 'financing.oldMonthlyInstallment'));
            return _get(price, 'common.isStrikeShown') && old != null ? text(euro(old)) : NONE;
        }, { desktopOnly: true }),
        row('financingTerms', euroIcon, ({ price }) => {
            const months = positive(_get(price, 'financing.paybackPeriod'));
            if (months == null || positive(_get(price, 'financing.monthlyInstallment')) == null)
                return NONE;
            const amount = euro(num(_get(price, 'financing.firstInstallment')) ?? 0);
            return text(t(ns('values.financingTerms'), { amount, months }));
        }, { small: true }),
        // leasing only where it is active for the car, as before ("---" otherwise)
        row('leasing', euroIcon, ({ price }) => {
            const rate = positive(_get(price, 'leasing.monthlyInstallment'));
            return _get(price, 'leasing.isActive') && rate != null ? { kind: 'rate', amount: euro(rate), unit } : NONE;
        }),
        row('leasingTerms', euroIcon, ({ price }) => {
            const months = positive(_get(price, 'leasing.paybackPeriod'));
            const mileage = positive(_get(price, 'leasing.annualMileage'));
            if (!_get(price, 'leasing.isActive') || months == null || mileage == null)
                return NONE;
            const amount = euro(num(_get(price, 'leasing.firstInstallment')) ?? 0);
            return text(t(ns('values.leasingTerms'), { amount, mileage: mileage.toLocaleString('de-DE'), months }));
        }, { small: true }),
        row('price', tagIcon, ({ price }) => {
            const value = positive(_get(price, 'buy.currentSalesPriceExtra'));
            return value != null ? text(euro(value)) : NONE;
        }),
        // list price minus sale price, behind the same strike flag as the UVP-Rate
        row('savings', tagIcon, ({ price }) => {
            const current = positive(_get(price, 'buy.currentSalesPriceExtra'));
            const highest = positive(_get(price, 'buy.highestPriceExtra'));
            return _get(price, 'common.isStrikeShown') && current != null && highest != null && highest > current
                ? text(`−${euro(highest - current)}`)
                : NONE;
        }, { desktopOnly: true }),
        // the SRP card's Preisbewertung (toAutobahnCar): the sale price against the predicted market
        // price, which without a prediction is getPredictablePrice's sale price × index, as on the card
        row('rating', starIcon, ({ details }) => {
            const current = positive(_get(details, 'price.currentSalesPrice'));
            if (current == null)
                return NONE;
            const predicted = getPredictablePrice(current, num(_get(details, 'price.mlCurrentSalesPricePredicted')));
            const { rating } = mapRating(current, predicted);
            return { kind: 'rating', label: t(`autobahn:tier.${rating}`), good: rating === 'top' || rating === 'good' };
        }, { desktopOnly: true }),
    ];
    const vehicleRows = [
        row('mileage', mileageIcon, ({ decorated }) => prop(decorated, 'mileage')),
        row('firstRegistration', registrationIcon, ({ decorated }) => prop(decorated, 'firstRegistration')),
        row('owners', ownersIcon, ({ details }) => {
            const owners = positive(_get(details, 'mainData.numberOfPreviousOwners'));
            return owners != null ? text(t('autobahn:card.specs.owners', { owners, count: owners })) : NONE;
        }),
        row('gearbox', gearboxIcon, ({ decorated }) => prop(decorated, 'gearbox')),
        row('power', powerIcon, ({ decorated }) => prop(decorated, 'power')),
        row('fuel', fuelIcon, ({ decorated }) => prop(decorated, 'fuel')),
        row('driveType', driveIcon, ({ decorated }) => prop(decorated, 'driveType')),
        row('body', doorIcon, ({ decorated }) => prop(decorated, 'category')),
        row('doorsSeats', doorIcon, ({ details }) => {
            const doors = positive(_get(details, 'mainData.doors'));
            const seats = positive(_get(details, 'mainData.seats'));
            return text([
                doors != null ? t(ns('values.doors'), { count: doors }) : '',
                seats != null ? t(ns('values.seats'), { count: seats }) : '',
            ].filter(Boolean).join(' · '));
        }),
        // the dealer's colour name, else the colour group
        row('color', colorIcon, ({ decorated }) => {
            const named = prop(decorated, 'manufacturerColorName');
            return isPresent(named) ? named : prop(decorated, 'exteriorColor');
        }),
        row('inspection', inspectionIcon, ({ decorated }) => prop(decorated, 'generalInspection')),
    ];
    const environmentRows = [
        row('consumption', leafIcon, ({ details }) => text(consumptionText(details.consumption))),
        row('co2', leafIcon, ({ details }) => {
            const co2 = positive(_get(details, 'environmentEmissions.wltpCo2')) ?? positive(_get(details, 'environmentEmissions.co2'));
            return co2 != null ? text(`${decimal(co2)} g/km`) : NONE;
        }),
        // the WLTP CO₂ class (Pkw-EnVKV), as on the SRP cards
        row('co2Class', leafIcon, ({ details }) => {
            const letter = co2ClassLetter(_get(details, 'environmentEmissions.wltpEnergyEfficiencyClass'));
            return letter ? { kind: 'co2', letter } : NONE;
        }),
    ];
    const conditionRows = [
        row('accident', engineIcon, ({ decorated }) => prop(decorated, 'accidentDamaged')),
        row('availability', truckIcon, ({ decorated }) => prop(decorated, 'offerAvailability')),
        // "24 Fotos · 312 Aufrufe": the photos, and the views the VDP shows (stats totalCarImpCount)
        row('media', cameraIcon, ({ details, views }) => {
            const photos = positive(_get(details, 'metaData.imagesCount'));
            const seen = positive(views);
            return text([
                photos != null ? t(ns('values.photos'), { count: photos }) : '',
                seen != null ? t(ns('values.views'), { count: seen, views: seen.toLocaleString('de-DE') }) : '',
            ].filter(Boolean).join(' · '));
        }),
    ];
    const sellerRows = [
        row('location', pinIcon, ({ details }) => {
            const zip = _get(details, 'location.zipCode');
            const city = _get(details, 'location.city');
            return text([zip, city].filter((part) => part && part !== 'N/A').join(' '));
        }),
    ];
    const highlightRows = [
        row('equipment', sparkleIcon, ({ details }) => {
            const items = (getActualHighlights(details, t) || []).map((item) => item.description).filter(Boolean);
            return items.length ? { kind: 'chips', items } : NONE;
        }),
    ];
    return [
        { key: 'price', rows: priceRows },
        { key: 'vehicle', rows: vehicleRows },
        { key: 'environment', rows: environmentRows },
        { key: 'condition', rows: conditionRows },
        { key: 'seller', rows: sellerRows },
        { key: 'highlights', rows: highlightRows },
    ]
        .map((section) => ({
        key: section.key,
        title: t(ns(`sections.${section.key}`)),
        rows: section.rows.filter((r) => r.cells.some(isPresent)),
    }))
        .filter((section) => section.rows.length > 0);
};
